(function () {
  const currency = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });

  // Mesma regra do site original: preço nulo/zerado exibe "Indisponível"
  function formatPrice(value) {
    return value == null || Number(value) <= 0 ? 'Indisponível' : currency.format(Number(value));
  }

  // ---------------------------------------------------------------------------
  // Helpers de parse
  // ---------------------------------------------------------------------------

  function parseJsonScript(root, selector, fallback) {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[LEV build] JSON invalido', selector, error);
      return fallback;
    }
  }

  // Reduz uma URL do nav (ou a da página) ao pathname, sem barra final e sem
  // diferenca de maiusculas, para comparar a tab atual com a rota em que estamos.
  function normalizePath(url) {
    if (!url) return '';
    try {
      const path = new URL(url, window.location.origin).pathname;
      return path.replace(/\/+$/, '').toLowerCase() || '/';
    } catch (error) {
      return '';
    }
  }

  // ---------------------------------------------------------------------------
  // Merge produtos do KIT → data
  // Acessórios/serviços vêm como produtos Shopify referenciados no produto KIT
  // ([data-build-kit-products]). Preço, variante e estoque saem do cadastro.
  // ---------------------------------------------------------------------------

  function mergeKitProducts(data, kit, assets) {
    if (!kit) return data;
    const variantMap = { ...(data.variantMap || {}) };

    // Itens base (do JSON/metaobject) indexados por accessoryKey: fornecem o SKU
    // visual (imagem da camada da bike) e o zindex, que o produto Shopify não tem.
    const baseByKey = {};
    [...(data.accessories || []), ...(data.services || [])].forEach((item) => {
      if (item.accessoryKey) baseByKey[item.accessoryKey] = item;
    });

    // A chave de tudo aqui (imagem da camada, conflito, variantMap) é o SKU
    // legado do item base — o mesmo que a produção usa. Os produtos da loja não
    // têm SKU cadastrado, então quem manda é `base.sku`; o produto entra com o
    // que só ele sabe: variantId, preço e disponibilidade ao vivo.
    // Acessórios com combinações (Rack de Prancha, Cadeirinha Dianteira) não têm
    // `sku` no topo — o identificador está na primeira combinação.
    // `layerSku` é o cadastro do próprio produto e vem primeiro: é ele que
    // permite um acessório existir sem item base no JSON do tema.
    const skuOf = (entry, base) =>
      entry.layerSku ||
      entry.sku ||
      base.sku ||
      (base.combinations && base.combinations[0] && base.combinations[0].sku) ||
      null;

    // Casa cada combinação do item base (cor, nº de pranchas…) com a variante
    // do produto cujo título contenha o valor da opção. Sem correspondência,
    // usa a variante de referência.
    const pickVariant = (entry, combo) => {
      const variants = entry.variants || [];
      if (!variants.length) return entry;
      const values = Object.entries(combo)
        .filter(([key]) => key !== 'sku')
        .map(([, value]) => String(value).toLowerCase())
        .filter(Boolean);
      const hit = variants.find((variant) =>
        values.some((value) => String(variant.title || '').toLowerCase().includes(value))
      );
      return hit || variants.find((variant) => !variant.soldOut) || variants[0];
    };

    const mapList = (list) =>
      (list || [])
        .map((entry) => ({ entry, base: baseByKey[entry.accessoryKey] || {} }))
        .filter(({ entry, base }) => skuOf(entry, base) && !entry.soldOut)
        .map(({ entry, base }) => {
          const sku = skuOf(entry, base);
          const combos = base.combinations || [];

          // Imagem da camada cadastrada no produto entra no mapa de assets pelo
          // código dela — mesma mecânica das cores.
          if (entry.layerImage && assets) assets[sku] = entry.layerImage;

          if (combos.length) {
            combos.forEach((combo) => {
              const variant = pickVariant(entry, combo);
              // Camada da variante ganha da do produto: é ela que muda com a
              // opção escolhida. Sem cadastro, o asset do tema segue valendo.
              if (variant.layerImage && assets) assets[combo.sku] = variant.layerImage;
              variantMap[combo.sku] = {
                id: variant.variantId || entry.variantId,
                price: variant.price != null ? variant.price : entry.price,
                lead_time: variant.leadTime || entry.leadTime || 0,
              };
            });
          } else {
            variantMap[sku] = {
              id: entry.variantId,
              price: entry.price,
              lead_time: entry.leadTime || 0,
            };
          }

          return {
            // O item base manda no que é curadoria (nome, descrição, opções de
            // cor, zindex da camada) — é o que a referência mostra. O cadastro
            // do produto entra só como fonte viva de preço/variante/estoque.
            ...base,
            name: base.name || entry.name,
            sku,
            accessoryKey: entry.accessoryKey,
            // `entry.group` vem do campo de categoria do KIT e ganha do base:
            // arrastar o produto para "Cestas" no admin tem que mover o card,
            // senão a curadoria antiga do JSON manda e o arrasto não faz nada.
            category: entry.group || base.category || entry.category || 'Acessórios',
            description: base.description || entry.description,
            fullDescription: entry.fullDescription || base.fullDescription,
            image: base.image || entry.image,
            imageSku: combos[0]?.sku || base.sku || sku,
            zindex: base.zindex || entry.layerZindex || 0,
            incompatible: base.incompatible || entry.incompatible || [],
          };
        });

    const accessories = mapList(kit.accessories);
    const services = mapList(kit.services);

    // `kit.explicit` só vem quando alguém escolheu os acessórios no admin
    // (produto ou modelo). O mapa por handle do template não marca, porque ali
    // a lista é do tema e a regra por modelo continua sendo a curadoria.
    const compatibilityRules =
      kit.explicit && data.compatibilityRules
        ? { ...data.compatibilityRules, explicitSelection: true }
        : data.compatibilityRules;

    return {
      ...data,
      compatibilityRules,
      accessories: accessories.length ? accessories : data.accessories,
      services: services.length ? services : data.services,
      variantMap,
    };
  }

  // ---------------------------------------------------------------------------
  // Peças vindas de produto ([data-build-pieces])
  // O produto pai escolhe um produto por peça; cada variante é uma opção. Nome,
  // preço, estoque e a camada que desenha na bike saem da variante — não há
  // cadastro paralelo, nem código de camada, nem imagem no tema.
  // ---------------------------------------------------------------------------

  // Cada opção do produto vira uma dimensão da peça. O nome vem do cadastro,
  // então a comparação ignora acento e caixa — "Elevações" e "elevacoes" são a
  // mesma coisa.
  // Gambiarra assumida: variante do Shopify não guarda cor, e a imagem dela é a
  // peça inteira (vira borrão num círculo de 36px). Até alguém configurar a
  // amostra nativa por valor de opção, o hex vem desta tabela pelo nome.
  //
  // Os cinco primeiros saíram do cruiser-build-data.json, que veio da produção,
  // então batem com o que está no ar. O resto é aproximação de bom senso.
  //
  // Nome fora da tabela não quebra: cai no chip com o nome escrito. O dia que a
  // amostra nativa existir, isto sai inteiro.
  const SWATCH_HEX = {
    preto: '#000000',
    cinza: '#8C8B8D',
    branco: '#FFFFFF',
    verde: '#5C6139',
    'verde militar': '#5C6139',
    vermelho: '#FF0B03',
    marrom: '#875945',
    caramelo: '#C2735A',
    bege: '#F5F5DC',
    creme: '#EFE7D6',
    prata: '#C0C0C0',
    grafite: '#3A3A3A',
    azul: '#1D3C8C',
    amarelo: '#F5C518',
    laranja: '#E8622A',
    rosa: '#E38AA8',
    roxo: '#6B3FA0',
    dourado: '#C9A227',
    // LEV-124 — a paleta do quadro da Mini, lida cor a cor em
    // golev.com.br/build/mini-build (cliquei cada amostra e li o rótulo
    // "Cor X" junto). "Lilás" não estava aqui e nem casava pelo primeiro
    // nome, então caía no chip escrito — o print do chamado.
    // As compostas vêm antes do fallback de primeira palavra de propósito:
    // sem elas "Verde Menta" e "Verde Musgo" herdavam o verde militar
    // (#5C6139) e as três apareciam iguais.
    lilas: '#c6b8db',
    nude: '#D3BBA8',
    'off white': '#ffffff',
    'cinza chumbo': '#959492',
    'verde menta': '#B8DBD4',
    'verde musgo': '#A5AA96',
    'azul pastel': '#B7DDE3',
    // Sem referência: a produção não cadastra laranja pastel no quadro.
    'laranja pastel': '#F0B49A',
  };

  function swatchHexFor(name) {
    const needle = normalizeOptionName(name);
    if (SWATCH_HEX[needle]) return SWATCH_HEX[needle];
    // "Verde Militar" cai no "verde"; "Preto Fosco" cai no "preto".
    const first = needle.split(/\s+/)[0];
    return SWATCH_HEX[first] || null;
  }

  const PIECE_OPTION_KEYS = [
    { key: 'color', match: ['cor', 'color', 'colour'] },
    // LEV-22: o Banco - KIT cadastra o tamanho como OPÇÃO DE VARIANTE (Cor +
    // Tamanho), não como produtos separados. Sem esta linha o valor caía no
    // `return null` abaixo e sumia: as quatro variantes viravam duas cores
    // duplicadas, o passo "Tamanho" nunca era desenhado e o M (R$ 468) ficava
    // inalcançável — o "banco não reflete no produto" que o Italo abriu.
    { key: 'size', match: ['tamanho', 'size'] },
    { key: 'height', match: ['elevacoes', 'elevacao', 'altura'] },
    { key: 'apoiador', match: ['apoiador de maos', 'apoiador'] },
    { key: 'encosto', match: ['encosto'] },
    { key: 'boards', match: ['pranchas', 'capacidade de pranchas'] },
  ];

  function normalizeOptionName(name) {
    return String(name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function pieceOptionKey(name) {
    const needle = normalizeOptionName(name);
    const hit = PIECE_OPTION_KEYS.find((entry) => entry.match.includes(needle));
    return hit ? hit.key : null;
  }

  // ---------------------------------------------------------------------------
  // Complementos da peça (Alturas / Encosto / Apoiador de Mãos)
  //
  // Altura, encosto e apoiador são dimensões do banco na tela, mas não cabem
  // como opção de variante: o Shopify aceita 3 opções por produto e o Banco -
  // KIT já usa duas (Cor, Tamanho). Cada um é um produto da loja, escolhido nos
  // campos do PRÓPRIO produto do banco, e é aqui que a combinação se forma: as
  // opções aparecem no card, o preço soma e cada complemento vira uma linha
  // própria no carrinho.
  // ---------------------------------------------------------------------------

  const SEAT_ADDON_NAME = { encosto: 'Encosto', apoiador: 'Apoiador de Mãos' };

  // "4cm - para pessoas de até 1,70m - 1,80m" → curto "4cm", longo o resto.
  // Só o PRIMEIRO travessão separa: o rótulo longo tem os seus ("1,70m - 1,80m").
  function splitOptionLabel(label) {
    const text = String(label || '').trim();
    if (!text) return null;
    const at = text.indexOf(' - ');
    if (at < 0) return { shortName: text, name: text };
    return { shortName: text.slice(0, at).trim(), name: text.slice(at + 3).trim() };
  }

  // Nome do arquivo do render → tokens comparáveis.
  // "banco-preto-g-4cm-encosto_1.webp" → ['banco','preto','g','4cm','encosto','1']
  // O "4 cm" separado vira "4cm": o cadastro não precisa acertar o hífen.
  function renderTokens(entry) {
    const source = entry.alt || String(entry.url || '').split('?')[0].split('/').pop() || '';
    const raw = decodeURIComponent(source)
      .replace(/\.[a-z0-9]+$/i, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
    const tokens = [];
    raw.forEach((token) => {
      if (token === 'cm' && /^\d+$/.test(tokens[tokens.length - 1] || '')) {
        tokens[tokens.length - 1] += 'cm';
        return;
      }
      tokens.push(token);
    });
    return tokens;
  }

  // Sem "Rótulo no configurador" no produto, o curto sai do título:
  // "Elevação Cruiser 4CM" → "4cm".
  function heightShortNameOf(addon) {
    if (addon.value) return addon.value;
    const fromLabel = splitOptionLabel(addon.label);
    if (fromLabel) return fromLabel.shortName;
    const fromTitle = String(addon.title || '').match(/(\d+)\s*cm/i);
    return fromTitle ? `${fromTitle[1]}cm` : String(addon.title || '').trim();
  }

  // O complemento pode ter cor própria (o Encosto tem Preto e Marrom) e quem
  // manda é a cor do banco. Sem cor no cadastro, a única variante serve.
  function seatAddonVariant(addon, colorName) {
    const variants = (addon.variants || []).filter((variant) => !variant.soldOut);
    if (!variants.length) return null;
    const colorIndex = (addon.optionNames || []).findIndex(
      (optionName) => pieceOptionKey(optionName) === 'color',
    );
    if (colorIndex >= 0 && colorName) {
      const hit = variants.find((variant) => (variant.values || [])[colorIndex] === colorName);
      if (hit) return hit;
    }
    return variants[0];
  }

  function seatAddonSku(baseSku, heightShortName, encosto, apoiador) {
    // A combinação básica mantém o SKU da variante: sem complemento marcado
    // nada muda, e `variantMap`/`assets` continuam valendo como estavam.
    if (heightShortName === '0cm' && encosto !== 'Sim' && apoiador !== 'Sim') return baseSku;
    return [
      baseSku,
      heightShortName,
      encosto === 'Sim' ? 'enc' : '',
      apoiador === 'Sim' ? 'apo' : '',
    ]
      .filter(Boolean)
      .join('~');
  }

  function expandSeatAddons({
    data,
    legacy,
    heightBase,
    renders,
    options,
    combinations,
    addons,
    variantMap,
    assets,
  }) {
    const list = (Array.isArray(addons) ? addons : []).filter((addon) =>
      (addon.variants || []).some((variant) => !variant.soldOut),
    );
    // Opção que o produto já tem ganha do complemento: o dia que a elevação
    // virar a 3ª opção do banco no cadastro, o complemento vira ruído e não
    // duas fontes brigando pela mesma dimensão.
    const heights = options.height?.length ? [] : list.filter((addon) => addon.slot === 'height');
    const encostoAddon = options.encosto?.length
      ? null
      : list.find((addon) => addon.slot === 'encosto');
    const apoiadorAddon = options.apoiador?.length
      ? null
      : list.find((addon) => addon.slot === 'apoiador');
    if (!heights.length && !encostoAddon && !apoiadorAddon) return combinations;

    // Rótulo da altura: primeiro o "Rótulo no configurador" do produto, depois
    // o do site original (cruiser-build-data.json), e por último o próprio
    // curto — assim o cadastro manda, mas nada fica sem nome.
    const legacyHeights = legacy?.options?.height || [];
    const heightEntry = (shortName, label) => {
      const fromLabel = splitOptionLabel(label);
      return {
        name:
          fromLabel?.name ||
          legacyHeights.find((entry) => entry.shortName === shortName)?.name ||
          shortName,
        shortName,
      };
    };

    // O banco sem elevação é a única altura sem produto — é o banco puro. O
    // rótulo dele é o campo "Altura base" do próprio banco; o curto continua
    // sendo "0cm", que é como o resto do configurador reconhece "sem elevação"
    // (o botão do M, o Resumo e a escolha do render).
    const heightOptions = [heightEntry('0cm', heightBase)];
    heights.forEach((addon) => heightOptions.push(heightEntry(heightShortNameOf(addon), addon.label)));
    if (heights.length) options.height = heightOptions;
    if (encostoAddon) options.encosto = [{ name: 'Não' }, { name: 'Sim' }];
    if (apoiadorAddon) options.apoiador = [{ name: 'Não' }, { name: 'Sim' }];

    // A peça inteira é UMA camada, então "banco com encosto" é um render
    // próprio — não dá para empilhar a foto do encosto por cima.
    //
    // O render vem do campo "Renders" do produto, casado pelo NOME DO ARQUIVO:
    // `banco-preto-g-4cm-encosto.webp`. Cor, tamanho e altura em qualquer
    // ordem; `encosto`/`apoiador` só aparecem quando ligados, e é por isso que
    // a ausência também é verificada — senão o arquivo do banco puro casaria
    // com a combinação com encosto.
    const renderIndex = (renders || []).map((entry) => ({
      url: entry.url,
      tokens: renderTokens(entry),
    }));

    const productRender = (colorName, sizeShortName, heightShortName, encosto, apoiador) => {
      const colorTokens = normalizeOptionName(colorName).split(/\s+/).filter(Boolean);
      const hit = renderIndex.find(({ tokens }) => {
        if (!colorTokens.every((token) => tokens.includes(token))) return false;
        if (!tokens.includes(sizeShortName.toLowerCase())) return false;
        if (!tokens.includes(heightShortName.toLowerCase())) return false;
        return tokens.includes('encosto') === (encosto === 'Sim')
          && tokens.includes('apoiador') === (apoiador === 'Sim');
      });
      return hit?.url || null;
    };

    // Sobra enquanto os renders não estiverem no produto: os 21 da produção são
    // asset do tema, indexados pelo código legado, e o JSON legado sabe qual
    // código é qual combinação. Código que também aparece em outra peça não
    // conta — no Cruiser o "11" está no banco E no pneu marrom, e é do pneu.
    const legacySkusElsewhere = new Set();
    (data?.main || []).forEach((group) => {
      if (group.name === legacy?.name) return;
      (group.combinations || []).forEach((combination) => legacySkusElsewhere.add(String(combination.sku)));
    });

    const legacyImage = (colorName, sizeShortName, heightShortName, encosto, apoiador) => {
      const hit = (legacy?.combinations || []).find(
        (combination) =>
          !legacySkusElsewhere.has(String(combination.sku)) &&
          combination.color === colorName &&
          String(combination.size || '').split(' - ')[0].trim().toUpperCase() === sizeShortName &&
          (legacyHeights.find((entry) => entry.name === combination.height)?.shortName ||
            combination.height) === heightShortName &&
          (combination.encosto || 'Não') === encosto &&
          (combination.apoiador || 'Não') === apoiador,
      );
      return hit ? assets[String(hit.sku)] : null;
    };

    const encostoValues = encostoAddon ? ['Não', 'Sim'] : ['Não'];
    const apoiadorValues = apoiadorAddon ? ['Não', 'Sim'] : ['Não'];
    const expanded = [];

    combinations.forEach((base) => {
      const baseInfo = variantMap[base.sku] || {};
      const sizeShortName = String(base.size || '')
        .split(' - ')[0]
        .trim()
        .toUpperCase();
      // O banco individual (M) vem sozinho na produção: sem elevação, sem
      // encosto e sem apoiador. É a mesma regra que o card já aplica na tela.
      const isIndividual = sizeShortName === 'M';

      heightOptions.forEach((height) => {
        encostoValues.forEach((encosto) => {
          apoiadorValues.forEach((apoiador) => {
            if (encosto === 'Sim' && apoiador === 'Sim') return;
            if (isIndividual && (height.shortName !== '0cm' || encosto === 'Sim' || apoiador === 'Sim')) {
              return;
            }

            const picked = [];
            if (height.shortName !== '0cm') {
              const addon = heights.find((entry) => heightShortNameOf(entry) === height.shortName);
              // O nome da linha do carrinho é o do produto — quem lê é o
              // estoque e a montagem.
              picked.push({ addon, name: addon?.title || `Elevação ${height.shortName}` });
            }
            if (encosto === 'Sim') {
              picked.push({ addon: encostoAddon, name: encostoAddon.title || SEAT_ADDON_NAME.encosto });
            }
            if (apoiador === 'Sim') {
              picked.push({ addon: apoiadorAddon, name: apoiadorAddon.title || SEAT_ADDON_NAME.apoiador });
            }

            let price = Number(baseInfo.price || 0);
            let leadTime = Number(baseInfo.lead_time || 0);
            const addonItems = [];
            const usable = picked.every(({ addon, name }) => {
              const variant = addon && seatAddonVariant(addon, base.color);
              if (!variant) return false;
              price += Number(variant.price || 0);
              leadTime = Math.max(leadTime, Number(addon.leadTime) || 0);
              addonItems.push({ id: variant.variantId, name });
              return true;
            });
            // Complemento esgotado não vira opção — o carrinho recusaria a
            // linha e o cliente só descobriria em inglês no Finalizar.
            if (!usable) return;

            const sku = seatAddonSku(base.sku, height.shortName, encosto, apoiador);
            variantMap[sku] = {
              id: baseInfo.id,
              price,
              lead_time: leadTime,
              addons: addonItems,
            };
            // Produto primeiro; o JSON da produção é só a sobra, e a
            // variante (Cor × Tamanho) é o último recurso.
            const image =
              productRender(base.color, sizeShortName, height.shortName, encosto, apoiador) ||
              legacyImage(base.color, sizeShortName, height.shortName, encosto, apoiador);
            if (image || assets[base.sku]) assets[sku] = image || assets[base.sku];

            expanded.push({
              ...base,
              sku,
              height: height.name,
              encosto,
              apoiador,
            });
          });
        });
      });
    });

    return expanded.length ? expanded : combinations;
  }

  function applyPieceProducts(data, pieces, assets, variantMap, seatAddons) {
    if (!Array.isArray(pieces) || !pieces.length) return data;

    const legacyByName = {};
    (data.main || []).forEach((group) => {
      legacyByName[group.name] = group;
    });

    const byName = {};
    pieces.forEach((piece) => {
      // LEV-22: variante esgotada não pode virar opção.
      // O snippet já emite `soldOut` por variante, mas ninguém lia: a cor
      // aparecia no card, entrava no Resumo e só a API do carrinho reclamava,
      // em inglês, na hora de Finalizar ("Banco - KIT - Preto / G is already
      // sold out"). Acessórios do KIT já filtravam assim em `mapList`.
      const products = (piece.products || [])
        .map((entry) => ({
          ...entry,
          variants: (entry.variants || []).filter((variant) => !variant.soldOut),
        }))
        .filter((entry) => entry.variants.length);
      if (!products.length) return;

      // Mais de um produto no mesmo grupo É uma dimensão: no banco, "Duplo" e
      // "Médio" são produtos separados, e escolher entre eles é o tamanho.
      //
      // Mas quando o tamanho já é opção de variante (Banco - KIT: Cor +
      // Tamanho), quem manda é a variante — senão as duas fontes se
      // atropelariam, com `options.sizes` cheio de títulos de produto e as
      // combinações carregando os valores da variante, e nada casaria.
      const hasVariantSize = products.some((entry) =>
        (entry.optionNames || []).some((optionName) => pieceOptionKey(optionName) === 'size'),
      );
      const hasSizes = !hasVariantSize && products.length > 1;
      const combinations = [];
      const values = { color: [], size: [], height: [], encosto: [], apoiador: [], boards: [] };
      const sizes = [];
      let leadTime = 0;

      products.forEach((entry) => {
        leadTime = Math.max(leadTime, Number(entry.leadTime) || 0);
        if (hasSizes && !sizes.some((size) => size.name === entry.title)) {
          sizes.push({
            name: entry.title,
            shortName: entry.title.split('-').pop().trim(),
            image: entry.image,
          });
        }

        (entry.variants || []).forEach((variant) => {
          const sku = String(variant.sku);
          if (variant.image && assets) assets[sku] = variant.image;
          variantMap[sku] = {
            id: variant.variantId,
            price: variant.price,
            lead_time: Number(entry.leadTime) || 0,
          };

          const combo = { sku };
          if (hasSizes) combo.size = entry.title;
          (entry.optionNames || []).forEach((optionName, index) => {
            const key = pieceOptionKey(optionName);
            if (!key) return;
            const value = (variant.values || [])[index];
            if (!value) return;
            combo[key] = value;
            const bucket = values[key];
            if (bucket && !bucket.some((item) => item.name === value)) bucket.push({ name: value });
          });
          combinations.push(combo);
        });
      });

      const options = {};
      if (values.color.length) {
        // O `hex` é a identidade da cor no resto do JS. Variante do Shopify não
        // tem cor, então o próprio nome faz esse papel e o swatch vira chip.
        options.colors = values.color.map((item) => {
          const swatch = swatchHexFor(item.name);
          return {
            name: item.name,
            // `hex` continua sendo o NOME porque é a identidade da cor no resto
            // do JS. Usar o hex de verdade aqui faria duas cores de mesmo tom
            // colidirem. Quem pinta é `swatch`.
            hex: item.name,
            swatch,
            noSwatch: !swatch,
          };
        });
      }
      if (values.size.length) {
        // O `shortName` é o que governa as regras do banco (altura travada no M,
        // encosto/apoiador só no G) e é o rótulo do botão. O cadastro pode
        // escrever só "M" ou o nome longo "M - Banco Individual"; nos dois casos
        // o curto é o que vem antes do travessão.
        options.sizes = values.size.map((entry) => ({
          name: entry.name,
          shortName: entry.name.split(' - ')[0].trim(),
        }));
      } else if (sizes.length) {
        options.sizes = sizes;
      }
      ['height', 'encosto', 'apoiador', 'boards'].forEach((key) => {
        if (values[key].length) options[key] = values[key];
      });

      // Altura, encosto e apoiador entram aqui: são produtos à parte, e é esta
      // chamada que os transforma em dimensão da peça (LEV-22).
      //
      // Quem os lista é o produto da peça — o banco sabe qual é o encosto dele.
      // O mapa da section só entra enquanto os campos do produto estiverem
      // vazios, do mesmo jeito que `kit_product_map` cobre `kit_accessories`.
      const productAddons = products.flatMap((entry) => entry.addons || []);
      const pieceCombinations = expandSeatAddons({
        data,
        legacy: legacyByName[piece.name],
        heightBase: products.find((entry) => entry.heightBase)?.heightBase || '',
        renders: products.flatMap((entry) => entry.renders || []),
        options,
        combinations,
        addons: productAddons.length ? productAddons : seatAddons?.[piece.name],
        variantMap,
        assets,
      });

      byName[piece.name] = {
        name: piece.name,
        zindex: piece.zindex || 0,
        description: products[0].description || '',
        image: products[0].image || null,
        leadTime,
        required: true,
        options,
        combinations: pieceCombinations,
      };
    });

    if (!Object.keys(byName).length) return data;

    // Peça sem produto escolhido continua vindo de onde vinha.
    const main = (data.main || []).map((group) => byName[group.name] || group);
    Object.keys(byName).forEach((name) => {
      if (!main.some((group) => group.name === name)) main.push(byName[name]);
    });

    return { ...data, main };
  }

  // ---------------------------------------------------------------------------
  // Compatibilidade por modelo (nível coarso — allowed/blocked por accessoryKey)
  // ---------------------------------------------------------------------------

  function isItemAllowed(item, rules) {
    if (!rules) return true;
    // Quando o produto lista os acessórios, a lista É a regra: o que está
    // alocado aparece, o que não está nem chega aqui. Deixar a regra por modelo
    // agir por cima significaria o cliente marcar um acessório no admin e ele
    // sumir da tela sem explicação — foi o que acontecia com a Cadeirinha
    // Dianteira no Cruiser.
    if (rules.explicitSelection) return true;
    const key = item.accessoryKey;
    if (!key) return true; // main items não têm accessoryKey — sempre visíveis

    const { allowed = [], blocked = [] } = rules;

    if (allowed.length > 0) {
      return allowed.includes(key);
    }
    if (blocked.length > 0) {
      return !blocked.includes(key);
    }
    return true;
  }

  function cleanBlockedSelections(data, state) {
    const rules = data.compatibilityRules;
    if (!rules) return;
    getAllItems(data).forEach((item) => {
      if (!isItemAllowed(item, rules) && state.items[item.name]) {
        delete state.items[item.name];
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Helpers de itens e seleção
  // ---------------------------------------------------------------------------

  function getAllItems(data) {
    return [...(data.main || []), ...(data.accessories || []), ...(data.services || [])];
  }

  function findItem(data, name) {
    return getAllItems(data).find((item) => item.name === name);
  }

  function firstSku(item) {
    return item?.combinations?.[0]?.sku || item?.sku || '';
  }

  function colorName(item, hex) {
    return item.options?.colors?.find((color) => color.hex === hex)?.name || '';
  }

  function createInitialOptionState(item, selectedSku) {
    const match =
      item.combinations?.find((combination) => combination.sku === selectedSku) ||
      item.combinations?.[0] ||
      {};
    const options = item.options || {};
    const state = {};
    if (options.colors?.length) {
      const name = match.color || options.colors[0].name;
      state.colors = options.colors.find((color) => color.name === name)?.hex || options.colors[0].hex;
    }
    if (options.sizes?.length) state.sizes = match.size || options.sizes[0].name;
    if (options.height?.length) state.height = match.height || options.height[0].name;
    if (options.encosto?.length) state.encosto = match.encosto || options.encosto[0].name;
    if (options.apoiador?.length) state.apoiador = match.apoiador || options.apoiador[0].name;
    if (options.boards?.length) state.boards = match.boards || options.boards[0].name;
    return state;
  }

  // O tamanho do banco governa três regras: altura travada no M, encosto e
  // apoiador só no G. O nome vem do cadastro — "M - Banco Individual" no JSON
  // legado, só "M" no produto Banco - KIT —, então quem decide é o shortName.
  // Comparar a string inteira desligava as três regras assim que a peça passou
  // a sair do produto (LEV-22).
  function sizeShortNameOf(item, name) {
    if (!name) return '';
    const entry = item?.options?.sizes?.find((size) => size.name === name);
    return String(entry?.shortName || name).trim().toUpperCase();
  }

  function skuFromOptions(item, options) {
    if (!item.combinations?.length) return item.sku || '';
    const selectedColor = colorName(item, options?.colors);
    const match = item.combinations.find(
      (c) =>
        (!selectedColor || c.color === selectedColor) &&
        (!options?.sizes || !c.size || c.size === options.sizes) &&
        (!options?.height || !c.height || c.height === options.height) &&
        (!options?.encosto || !c.encosto || c.encosto === options.encosto) &&
        (!options?.apoiador || !c.apoiador || c.apoiador === options.apoiador) &&
        (!options?.boards || !c.boards || c.boards === options.boards),
    );
    return match?.sku || item.sku || item.combinations[0]?.sku || '';
  }

  function selectedCombination(item, sku) {
    return item.combinations?.find((combination) => combination.sku === sku);
  }

  function selectedLabel(item, sku) {
    const combination = selectedCombination(item, sku);
    if (!combination) return sku ? '' : '';
    const parts = [];
    if (combination.color) parts.push(combination.color);
    if (combination.size) {
      const size = item.options?.sizes?.find((entry) => entry.name === combination.size);
      parts.push(size?.shortName || combination.size.replace(' - ', ' '));
    }
    if (combination.height && sizeShortNameOf(item, combination.size) !== 'M') {
      const height = item.options?.height?.find((entry) => entry.name === combination.height);
      const shortName = height?.shortName || combination.height;
      if (shortName !== '0cm') parts.push(shortName);
    }
    if (combination.encosto === 'Sim') parts.push('Encosto');
    if (combination.apoiador === 'Sim') parts.push('Apoiador');
    if (combination.boards) parts.push(combination.boards);
    return parts.filter(Boolean).join(' ');
  }

  function getIncompatibleList(item, sku) {
    const combination = selectedCombination(item, sku);
    return combination?.incompatible || item.incompatible || [];
  }

  function findNameBySku(data, sku) {
    for (const item of getAllItems(data)) {
      if (item.sku === sku || item.combinations?.some((combination) => combination.sku === sku)) {
        return item.name;
      }
    }
    return `SKU ${sku}`;
  }

  // Conflitos em nível de SKU (incompatible[] por combinação — nível fino)
  function getConflicts(data, state, item) {
    const sku = state.items[item.name];
    if (!sku) return [];
    const selectedSkus = Object.values(state.items);
    return getIncompatibleList(item, sku)
      .filter((blockedSku) => selectedSkus.includes(blockedSku))
      .map((blockedSku) => findNameBySku(data, blockedSku));
  }

  function hasAnyConflict(data, state) {
    return Object.keys(state.items).some((name) => {
      const item = findItem(data, name);
      return item && getConflicts(data, state, item).length > 0;
    });
  }

  function variantInfo(data, sku) {
    const entry = data.variantMap?.[sku];
    if (!entry) return {};
    if (typeof entry === 'number' || typeof entry === 'string') return { id: entry };
    return entry;
  }

  function priceFor(data, sku) {
    const info = variantInfo(data, sku);
    const price = Number(info.price || (info.price_cents && info.price_cents / 100) || 0);
    return Number.isFinite(price) ? price : 0;
  }

  // No site original cada item selecionado soma 1 dia de montagem;
  // lead_time do variant map tem prioridade quando cadastrado.
  function leadTimeFor(data, sku) {
    const info = variantInfo(data, sku);
    const lead = Number(info.lead_time || info.leadTime || 0) || 0;
    return lead > 0 ? lead : 1;
  }

  function selectedEntries(data, state) {
    return Object.entries(state.items)
      .map(([name, sku]) => {
        const item = findItem(data, name);
        return item ? { item, sku } : null;
      })
      .filter(Boolean);
  }

  function initOptionStates(data, state) {
    getAllItems(data).forEach((item) => {
      if (!state.options[item.name]) {
        state.options[item.name] = createInitialOptionState(item, state.items[item.name] || firstSku(item));
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Utilidades de DOM
  // ---------------------------------------------------------------------------

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // ---------------------------------------------------------------------------
  // Exibição de erro amigável (substitui window.alert)
  // ---------------------------------------------------------------------------

  // A /cart/add.js responde na locale da loja, e a loja ainda responde em
  // inglês: "The product 'Banco - KIT - Preto / G' is already sold out."
  // aparecia crua na tela (LEV-22). Traduz o que a API manda; o que não estiver
  // mapeado cai numa frase em português em vez de vazar inglês.
  const CART_ERROR_PT = [
    [
      /^the product ['"“](.+)['"”] is already sold out\.?$/i,
      (match) => `O produto "${match[1]}" está esgotado.`,
    ],
    [
      /^all (\d+) (.+) are in your cart\.?$/i,
      (match) => `Você já tem as ${match[1]} unidades disponíveis de "${match[2]}" no carrinho.`,
    ],
    [
      /only (\d+) .*(?:left|in stock|available)/i,
      (match) => `Restam apenas ${match[1]} unidade(s) em estoque.`,
    ],
    [
      /cannot find variant/i,
      () => 'Não encontramos essa opção do produto. Recarregue a página e tente novamente.',
    ],
  ];

  function translateCartError(description) {
    const text = String(description || '').trim();
    const fallback = 'Não foi possível adicionar ao carrinho. Verifique a disponibilidade dos itens e tente novamente.';
    if (!text) return fallback;
    for (const [pattern, build] of CART_ERROR_PT) {
      const match = text.match(pattern);
      if (match) return build(match);
    }
    // Mensagem não mapeada com cara de inglês vira a genérica: melhor uma frase
    // vaga em português do que uma precisa em inglês.
    if (/\b(the|is|are|your|you|cannot|only|sold|stock|cart|product)\b/i.test(text)) return fallback;
    return text;
  }

  function showBuildError(root, message, { persistent = false } = {}) {
    const existing = root.querySelector('.lev-build__error');
    if (existing) existing.remove();
    const error = el('div', 'lev-build__error');
    error.setAttribute('role', 'alert');
    error.appendChild(el('span', '', message));
    const close = el('button', 'lev-build__error-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', 'Fechar aviso');
    close.addEventListener('click', () => error.remove());
    if (!persistent) error.appendChild(close);
    root.querySelector('.lev-build__panel')?.prepend(error);
    error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (!persistent) setTimeout(() => error.remove(), 9000);
    return error;
  }

  // ---------------------------------------------------------------------------
  // Bike preview — camadas persistentes por item; só o src da camada alterada
  // muda (sem recriar o DOM inteiro → sem flicker, igual ao site original)
  // ---------------------------------------------------------------------------

  function updateBike(root, data, state, assets) {
    const container = root.querySelector('[data-build-bike]');
    if (!container) return;
    const leftovers = new Map(
      [...container.querySelectorAll('img[data-item]')].map((img) => [img.dataset.item, img]),
    );

    selectedEntries(data, state).forEach(({ item, sku }) => {
      // Produtos do KIT têm SKU próprio (Shopify); a imagem da camada vem do
      // SKU visual herdado do item base (imageSku) quando o SKU não tem asset.
      const src = assets[sku] || assets[item.imageSku];
      let img = leftovers.get(item.name);
      if (!src) {
        if (img) img.remove();
        leftovers.delete(item.name);
        return;
      }
      if (!img) {
        img = document.createElement('img');
        img.dataset.item = item.name;
        img.alt = `${item.name} ${selectedLabel(item, sku)}`.trim();
        container.appendChild(img);
      } else {
        leftovers.delete(item.name);
      }
      if (img.dataset.sku !== sku) {
        img.src = src;
        img.dataset.sku = sku;
        img.alt = `${item.name} ${selectedLabel(item, sku)}`.trim();
      }
      // Only Banco needs size/height for CSS nudges — skip other layers.
      if (item.name === 'Banco') {
        const combination = selectedCombination(item, sku);
        const sizeShort = sizeShortNameOf(item, combination?.size) || '';
        const heightShort =
          item.options?.height?.find((entry) => entry.name === combination?.height)?.shortName || '';
        if (img.dataset.size !== sizeShort) {
          if (sizeShort) img.dataset.size = sizeShort;
          else delete img.dataset.size;
        }
        if (img.dataset.height !== heightShort) {
          if (heightShort) img.dataset.height = heightShort;
          else delete img.dataset.height;
        }
      }
      img.style.zIndex = String(item.zindex || 0);
    });

    leftovers.forEach((img) => img.remove());
  }

  // Pré-carrega em idle todas as imagens alcançáveis pela configuração atual
  function preloadAssets(data, assets) {
    const skus = new Set();
    getAllItems(data).forEach((item) => {
      if (item.sku) skus.add(item.sku);
      if (item.imageSku) skus.add(item.imageSku);
      (item.combinations || []).forEach((combination) => combination.sku && skus.add(combination.sku));
    });
    const sources = [...skus].map((sku) => assets[sku]).filter(Boolean);
    let index = 0;
    const loadNext = (deadline) => {
      while (index < sources.length && (!deadline || deadline.timeRemaining() > 4)) {
        const image = new Image();
        image.decoding = 'async';
        image.src = sources[index];
        index += 1;
      }
      if (index < sources.length) schedule();
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadNext, { timeout: 1500 });
      } else {
        setTimeout(() => loadNext(null), 180);
      }
    };
    schedule();
  }

  // ---------------------------------------------------------------------------
  // Navegação de modelos (setas + fades controlados pelo estado de scroll)
  // ---------------------------------------------------------------------------

  function setupModels(root, data) {
    const nav = root.querySelector('[data-build-models]');
    const box = root.querySelector('[data-models-box]');
    const prev = root.querySelector('[data-model-prev]');
    const next = root.querySelector('[data-model-next]');
    if (!nav) return;

    // O nav é o mesmo em todos os modelos, então o flag `active` do JSON não serve
    // para marcar a tab atual — ela vem da URL da página. O flag fica só de fallback
    // para quando a URL não bate com nenhuma entrada (preview, rota legada, etc).
    const currentPath = normalizePath(window.location.pathname);
    const navMatchesUrl = (data.nav || []).some((model) => normalizePath(model.url) === currentPath);

    nav.innerHTML = '';
    (data.nav || []).forEach((model, index) => {
      if (index > 0) nav.appendChild(el('span', 'lev-build__model-divider'));
      const isActive = navMatchesUrl ? normalizePath(model.url) === currentPath : !!model.active;
      const link = document.createElement('a');
      link.className = `lev-build__model${isActive ? ' is-active' : ''}`;
      link.href = model.url || '#';
      link.setAttribute('aria-label', model.title || model.label);
      if (isActive) link.setAttribute('aria-current', 'page');
      link.appendChild(el('span', 'lev-build__model-text', model.label));
      nav.appendChild(link);
    });

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = nav;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const showLeft = !(scrollLeft <= 2) && maxScroll > 0;
      const showRight = !(scrollLeft + clientWidth >= scrollWidth - 2) && maxScroll > 0;
      if (prev) prev.hidden = !showLeft;
      if (next) next.hidden = !showRight;
      if (box) {
        box.classList.toggle('has-left-fade', showLeft);
        box.classList.toggle('has-right-fade', showRight);
      }
    };

    nav.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    [50, 200, 500, 1000].forEach((delay) => setTimeout(updateArrows, delay));
    requestAnimationFrame(updateArrows);

    const step = () => Math.max(nav.clientWidth / 5, 120);
    prev?.addEventListener('click', () => {
      nav.scrollBy({ left: -step(), behavior: 'smooth' });
      requestAnimationFrame(updateArrows);
      setTimeout(updateArrows, 220);
    });
    next?.addEventListener('click', () => {
      nav.scrollBy({ left: step(), behavior: 'smooth' });
      requestAnimationFrame(updateArrows);
      setTimeout(updateArrows, 220);
    });
  }

  // ---------------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------------

  function setupTabs(root, data, state, onTabChange) {
    const tabs = root.querySelector('[data-build-tabs]');
    tabs.innerHTML = '';
    const labels = data.tabs || ['Bike', 'Acessórios', 'Resumo'];
    const nodes = labels.map((label, index) => {
      const tab = el('div', 'lev-build__tab');
      tab.setAttribute('role', 'button');
      tab.tabIndex = 0;
      tab.appendChild(el('span', '', `${index + 1}.${label}`));
      tab.addEventListener('click', () => onTabChange(index));
      tab.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') onTabChange(index);
      });
      tabs.appendChild(tab);
      return tab;
    });
    return () => {
      nodes.forEach((node, index) => node.classList.toggle('is-active', index === state.tab));
    };
  }

  // ---------------------------------------------------------------------------
  // Modal (réplica do ModalBuild: overlay + X + galeria + "Descrição")
  // ---------------------------------------------------------------------------

  function openModal(root, item, imageSrc) {
    const modal = el('div', 'lev-build__modal');
    const card = el('div', 'lev-build__modal-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', item.name);

    const header = el('div', 'lev-build__modal-header');
    const close = el('button', 'lev-build__modal-close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Fechar');
    close.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => modal.remove());
    header.appendChild(close);
    card.appendChild(header);

    const content = el('div', 'lev-build__modal-content');
    if (imageSrc) {
      const gallery = el('div', 'lev-build__modal-gallery');
      const frame = el('div', 'lev-build__modal-gallery-frame');
      const image = document.createElement('img');
      image.src = imageSrc;
      image.alt = item.name;
      frame.appendChild(image);
      gallery.appendChild(frame);
      content.appendChild(gallery);
    }

    const body = el('div', 'lev-build__modal-body');
    body.appendChild(el('h3', 'lev-build__modal-body-title', 'Descrição'));
    body.appendChild(
      el('p', 'lev-build__modal-description', item.fullDescription || item.description || ''),
    );
    content.appendChild(body);
    card.appendChild(content);
    modal.appendChild(card);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.remove();
    });
    root.appendChild(modal);
  }

  // ---------------------------------------------------------------------------
  // Card de item (BuildSelectorItem)
  // isMainItem: cards da aba 1.Bike (sem toggle, com chevron, expande no clique)
  // showToggle: acessórios (toggle liga/desliga, preço e Sobre no cabeçalho)
  // ---------------------------------------------------------------------------

  function createItemCard(ctx, item, opts) {
    const { root, data, state, sync, assets } = ctx;
    const showToggle = Boolean(opts.showToggle);
    const isMainItem = Boolean(opts.isMainItem);
    // Peça da bike é quem está em `data.main`, que sai dos seletores do produto
    // pai. Era uma lista fixa ['Quadro','Banco','Pneu'] — quebrava no dia que
    // uma peça nova entrasse.
    const isBikeAccessory = (data.main || []).some((group) => group.name === item.name);
    const options = item.options || {};

    // LEV-22: peça removida no Resumo precisa voltar a ser escolhível.
    // O card da aba 1.Bike não tem toggle — a única forma de (re)entrar na
    // configuração é clicar numa opção. Antes, todo handler de opção só
    // reescrevia o SKU `if (state.items[item.name])`, então depois da lixeira do
    // Resumo Quadro/Pneu/Banco viravam cards mortos: sem cor, sem preço, sem
    // volta. Para peça da bike, escolher uma opção seleciona o item.
    const chooseOption = (current) => {
      if (isMainItem || state.items[item.name]) {
        state.items[item.name] = skuFromOptions(item, current);
      }
    };

    const card = el(
      'article',
      `lev-build__card${isBikeAccessory ? ' is-bike-accessory' : ''}${isMainItem ? ' is-main-item' : ''}`,
    );

    // --- Cabeçalho -----------------------------------------------------------
    const head = el('div', 'lev-build__card-head');

    let toggle = null;
    if (showToggle) {
      toggle = el('span', 'lev-build__toggle');
      toggle.appendChild(el('span', 'lev-build__toggle-slider'));
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (state.items[item.name]) {
          const conflicts = getConflicts(data, state, item);
          if (!item.required || conflicts.length) delete state.items[item.name];
        } else {
          state.items[item.name] = skuFromOptions(item, state.options[item.name]);
        }
        sync();
      });
      head.appendChild(toggle);
    }

    const titleContainer = el(
      'div',
      `lev-build__title-container${showToggle ? ' is-accessory' : ''}${isBikeAccessory ? ' has-underline' : ''}`,
    );
    titleContainer.appendChild(el('h5', 'lev-build__title', item.name));
    head.appendChild(titleContainer);

    let chevron = null;
    let headPrice = null;
    if (!showToggle) {
      chevron = el('div', 'lev-build__chevron');
      chevron.addEventListener('click', (event) => {
        event.stopPropagation();
        state.open[item.name] = !state.open[item.name];
        sync();
      });
      head.appendChild(chevron);
    } else {
      const priceContainer = el('div', 'lev-build__price-container');
      headPrice = el('span', 'lev-build__price-text');
      priceContainer.appendChild(headPrice);
      head.appendChild(priceContainer);

      const about = el('button', 'lev-build__about', 'Sobre');
      about.type = 'button';
      about.addEventListener('click', (event) => {
        event.stopPropagation();
        openModal(root, item, item.image || assets[state.items[item.name] || firstSku(item)] || assets[item.imageSku]);
      });
      head.appendChild(about);
    }

    card.appendChild(head);

    if (!showToggle) {
      card.addEventListener('click', () => {
        state.open[item.name] = !state.open[item.name];
        sync();
      });
    }

    // --- Cores (visível sempre que o item está selecionado) ------------------
    let colorsWrap = null;
    let colorBold = null;
    const swatchOuters = new Map();
    if (options.colors?.length && item.combinations?.length) {
      colorsWrap = el('div', 'lev-build__colors');
      const optionContainer = el('div', 'lev-build__option');
      optionContainer.addEventListener('click', (event) => event.stopPropagation());
      const label = el('div', 'lev-build__label');
      label.append('Cor ');
      colorBold = el('strong');
      label.appendChild(colorBold);
      optionContainer.appendChild(label);

      const picker = el('div', 'lev-build__swatches');
      options.colors.forEach((color) => {
        const outer = el('div', 'lev-build__swatch-outer');
        const inner = el('button', 'lev-build__swatch');
        inner.type = 'button';
        if (color.swatch) {
          inner.style.backgroundColor = color.swatch;
        } else if (color.noSwatch) {
          // Variante do Shopify não tem cor, e a imagem dela é a peça inteira —
          // vira um borrão dentro de um círculo de 24px. Enquanto ninguém
          // configurar a amostra nativa por valor de opção, o nome é o que
          // comunica. Vira chip de texto em vez de círculo.
          inner.classList.add('lev-build__swatch--label');
          inner.textContent = color.name;
        } else {
          inner.style.backgroundColor = color.hex;
        }
        inner.setAttribute('aria-label', `Cor ${color.name}`);
        inner.addEventListener('click', (event) => {
          event.stopPropagation();
          const current = state.options[item.name];
          if (current.colors === color.hex && state.items[item.name]) return;
          current.colors = color.hex;
          chooseOption(current);
          sync();
        });
        outer.appendChild(inner);
        picker.appendChild(outer);
        swatchOuters.set(color.hex, outer);
      });
      optionContainer.appendChild(picker);
      colorsWrap.appendChild(optionContainer);
      card.appendChild(colorsWrap);
    }

    // --- Descrição + preço + Sobre (itens da bike, somente expandido) --------
    let description = null;
    let priceAbout = null;
    let bodyPrice = null;
    if (!showToggle) {
      description = el('p', 'lev-build__description', item.description || '');
      priceAbout = el('div', 'lev-build__price-about');
      const priceContainer = el('div', 'lev-build__price-container');
      bodyPrice = el('span', 'lev-build__price-text');
      priceContainer.appendChild(bodyPrice);
      priceAbout.appendChild(priceContainer);
      const about = el('button', 'lev-build__about', 'Sobre');
      about.type = 'button';
      about.addEventListener('click', (event) => {
        event.stopPropagation();
        openModal(root, item, item.image || assets[state.items[item.name] || firstSku(item)] || assets[item.imageSku]);
      });
      priceAbout.appendChild(about);
      if (colorsWrap) {
        colorsWrap.appendChild(description);
        colorsWrap.appendChild(priceAbout);
      } else {
        card.appendChild(description);
        card.appendChild(priceAbout);
      }
    }

    // --- Opções (Banco: tamanho/altura/encosto/apoiador) ----------------------
    const hasSeatOptions =
      item.combinations?.length && item.name !== 'Quadro' && item.name !== 'Pneu' &&
      (options.sizes?.length || options.height?.length);

    let optionsWrap = null;
    const seat = {};
    if (hasSeatOptions) {
      optionsWrap = el('div', 'lev-build__options');

      if (options.sizes?.length) {
        seat.sizeLines = el('div', 'lev-build__lines');
        const container = el('div', 'lev-build__option');
        container.addEventListener('click', (event) => event.stopPropagation());
        const label = el('div', 'lev-build__label');
        label.append('Tamanho ');
        seat.sizeBold = el('strong');
        label.appendChild(seat.sizeBold);
        container.appendChild(label);
        const group = el('div', 'lev-build__choice-group');
        seat.sizeButtons = new Map();
        // Ordem fixa M → G, como no site original
        const sizeOrder = { M: 0, G: 1 };
        const sortedSizes = [...options.sizes].sort(
          (a, b) => (sizeOrder[a.shortName] ?? 2) - (sizeOrder[b.shortName] ?? 2),
        );
        sortedSizes.forEach((size) => {
          const button = el('button', 'lev-build__choice', size.shortName || size.name);
          button.type = 'button';
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            const current = state.options[item.name];
            if (current.sizes === size.name && state.items[item.name]) return;
            current.sizes = size.name;
            if (sizeShortNameOf(item, size.name) === 'M') {
              const zero = options.height?.find((height) => height.shortName === '0cm');
              if (zero) current.height = zero.name;
              current.encosto = 'Não';
              current.apoiador = 'Não';
            }
            chooseOption(current);
            sync();
          });
          group.appendChild(button);
          seat.sizeButtons.set(size.name, button);
        });
        container.appendChild(group);
        seat.sizeLines.appendChild(container);
        optionsWrap.appendChild(seat.sizeLines);
      }

      if (options.height?.length) {
        seat.heightLines = el('div', 'lev-build__lines');
        const container = el('div', 'lev-build__option');
        container.addEventListener('click', (event) => event.stopPropagation());
        seat.heightLabel = el('div', 'lev-build__label');
        seat.heightBold = el('strong');
        container.appendChild(seat.heightLabel);
        seat.heightGroup = el('div', 'lev-build__choice-group');
        seat.heightButtons = new Map();
        options.height.forEach((height) => {
          const button = el('button', 'lev-build__height-choice', height.shortName || height.name);
          button.type = 'button';
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            const current = state.options[item.name];
            if (current.height === height.name && state.items[item.name]) return;
            current.height = height.name;
            chooseOption(current);
            sync();
          });
          seat.heightGroup.appendChild(button);
          seat.heightButtons.set(height.name, button);
        });
        container.appendChild(seat.heightGroup);
        seat.heightLines.appendChild(container);
        optionsWrap.appendChild(seat.heightLines);
      }

      const buildSeatToggleRow = (key, labelText) => {
        const lines = el('div', 'lev-build__lines');
        const container = el('div', 'lev-build__option');
        container.addEventListener('click', (event) => event.stopPropagation());
        const row = el('div', 'lev-build__toggle-row');
        const inner = el('div', 'lev-build__toggle-row-inner');
        const toggleNode = el('span', 'lev-build__toggle');
        toggleNode.appendChild(el('span', 'lev-build__toggle-slider'));
        toggleNode.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const current = state.options[item.name];
          const other = key === 'encosto' ? 'apoiador' : 'encosto';
          const next = current[key] === 'Sim' ? 'Não' : 'Sim';
          if (next === 'Sim' && current[other] === 'Sim') {
            state.bancoWarning[item.name] = key;
            clearTimeout(seat.warningTimer);
            seat.warningTimer = setTimeout(() => {
              state.bancoWarning[item.name] = null;
              sync();
            }, 15000);
            sync();
            return;
          }
          current[key] = next;
          state.bancoWarning[item.name] = null;
          chooseOption(current);
          sync();
        });
        inner.appendChild(toggleNode);
        const rowLabel = el('div', 'lev-build__label lev-build__toggle-label', labelText);
        inner.appendChild(rowLabel);
        row.appendChild(inner);
        container.appendChild(row);
        lines.appendChild(container);
        const warning = el('div', 'lev-build__note');
        lines.appendChild(warning);
        return { lines, toggle: toggleNode, warning };
      };

      if (options.encosto?.length) {
        seat.encosto = buildSeatToggleRow('encosto', 'Encosto');
        optionsWrap.appendChild(seat.encosto.lines);
        seat.encostoStatic = el(
          'div',
          'lev-build__note',
          'OBS: o encosto só é compatível com o banco G - Duplo.',
        );
        optionsWrap.appendChild(seat.encostoStatic);
      }
      if (options.apoiador?.length) {
        seat.apoiador = buildSeatToggleRow('apoiador', 'Apoiador de Mãos');
        optionsWrap.appendChild(seat.apoiador.lines);
        seat.apoiadorStatic = el(
          'div',
          'lev-build__note',
          'OBS: o apoiador de mãos só é compatível com o banco G - Duplo.',
        );
        optionsWrap.appendChild(seat.apoiadorStatic);
      }

      card.appendChild(optionsWrap);
    }

    // --- Capacidade de pranchas (Rack de Prancha) ------------------------------
    let boards = null;
    if (options.boards?.length) {
      boards = { container: el('div', 'lev-build__option'), buttons: new Map() };
      boards.container.style.paddingTop = '10px';
      boards.container.addEventListener('click', (event) => event.stopPropagation());
      const label = el('div', 'lev-build__label');
      label.append('Capacidade de Pranchas ');
      boards.bold = el('strong');
      label.appendChild(boards.bold);
      boards.container.appendChild(label);
      const group = el('div', 'lev-build__choice-group');
      group.style.paddingTop = '10px';
      options.boards.forEach((board) => {
        const button = el('button', 'lev-build__choice', board.shortName || board.name);
        button.type = 'button';
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const current = state.options[item.name];
          if (current.boards === board.name && state.items[item.name]) return;
          current.boards = board.name;
          // Escolher a capacidade já liga o rack — os dois ramos do if
          // anterior faziam exatamente isto.
          state.items[item.name] = skuFromOptions(item, current);
          sync();
        });
        group.appendChild(button);
        boards.buttons.set(board.name, button);
      });
      boards.container.appendChild(group);
      card.appendChild(boards.container);
    }

    // --- Aviso de incompatibilidade -------------------------------------------
    const conflictNote = el('div', 'lev-build__note');

    card.appendChild(conflictNote);

    // --- Atualização granular ---------------------------------------------------
    function update() {
      const selected = Boolean(state.items[item.name]);
      const expanded = Boolean(state.open[item.name]) && !showToggle;
      const current = state.options[item.name] || {};
      const sku = state.items[item.name] || skuFromOptions(item, current) || firstSku(item);
      const price = priceFor(data, sku);

      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-open', expanded);
      if (toggle) toggle.classList.toggle('is-on', selected);
      if (headPrice) headPrice.textContent = formatPrice(price);
      if (bodyPrice) bodyPrice.textContent = formatPrice(price);

      if (colorsWrap) {
        // Peça da bike mostra as cores mesmo fora da configuração: é o clique
        // na cor que traz a peça de volta depois da lixeira do Resumo (LEV-22).
        colorsWrap.hidden = !selected && !isMainItem;
        if (colorBold) colorBold.textContent = colorName(item, current.colors);
        swatchOuters.forEach((outer, hex) => {
          // Sem o item na configuração nenhuma cor está escolhida — o anel
          // marcaria uma cor que não está no Resumo.
          outer.classList.toggle('is-selected', selected && hex === current.colors);
        });
      }

      if (description) description.hidden = !expanded;
      if (priceAbout) priceAbout.hidden = !expanded;

      if (optionsWrap) {
        // Mesma razão das cores: no card da peça (Banco) as opções continuam
        // acessíveis com o card aberto, mesmo que a peça esteja fora da
        // configuração — senão não há como recolocá-la (LEV-22).
        const showOptions = (selected || isMainItem) && (showToggle || expanded);
        optionsWrap.hidden = !showOptions;
        if (showOptions) {
          const sizeIsM = sizeShortNameOf(item, current.sizes) === 'M';
          const sizeIsG = sizeShortNameOf(item, current.sizes) === 'G';

          if (seat.sizeBold) seat.sizeBold.textContent = current.sizes || '';
          seat.sizeButtons?.forEach((button, name) => {
            button.classList.toggle('is-selected', name === current.sizes);
          });

          if (seat.heightLabel) {
            seat.heightLabel.textContent = sizeIsM
              ? 'Banco individual atende pessoas a partir de 1,60m'
              : 'Altura do Banco ';
            if (!sizeIsM) {
              seat.heightBold.textContent = current.height || '';
              seat.heightLabel.appendChild(seat.heightBold);
            }
            seat.heightButtons.forEach((button, name) => {
              const heightEntry = options.height.find((entry) => entry.name === name);
              button.hidden = sizeIsM && heightEntry?.shortName !== '0cm';
              button.classList.toggle('is-selected', name === current.height);
            });
          }

          const warningKey = state.bancoWarning[item.name];
          if (seat.encosto) {
            seat.encosto.lines.hidden = !sizeIsG;
            seat.encosto.toggle.classList.toggle('is-on', current.encosto === 'Sim');
            // Aviso do encosto aparece na seção do apoiador e vice-versa (igual ao original)
            seat.encosto.warning.hidden = warningKey !== 'apoiador';
            seat.encosto.warning.textContent = 'OBS: o apoiador de mãos não é compatível com o encosto.';
            seat.encostoStatic.hidden = sizeIsG;
          }
          if (seat.apoiador) {
            seat.apoiador.lines.hidden = !sizeIsG;
            seat.apoiador.toggle.classList.toggle('is-on', current.apoiador === 'Sim');
            seat.apoiador.warning.hidden = warningKey !== 'encosto';
            seat.apoiador.warning.textContent = 'OBS: o encosto não é compatível com o apoiador de mãos.';
            seat.apoiadorStatic.hidden = sizeIsG;
          }
        }
      }

      if (boards) {
        boards.bold.textContent = current.boards || '';
        boards.buttons.forEach((button, name) => {
          button.classList.toggle('is-selected', name === current.boards);
        });
      }

      const conflicts = selected ? getConflicts(data, state, item) : [];
      if (conflicts.length) {
        conflictNote.hidden = false;
        conflictNote.innerHTML = '';
        conflictNote.appendChild(el('div', '', 'Este item é incompatível com'));
        conflicts.forEach((name, index) => {
          conflictNote.appendChild(el('span', '', `${name}${index < conflicts.length - 1 ? ', ' : ''}`));
        });
      } else {
        conflictNote.hidden = true;
      }
    }

    return { el: card, update };
  }

  // ---------------------------------------------------------------------------
  // Categoria de acessórios (BuildSelectorCategory)
  // ---------------------------------------------------------------------------

  function createCategory(ctx, categoryName, items) {
    const { state, sync } = ctx;
    const box = el('div', 'lev-build__category');
    const head = el('button', 'lev-build__category-head');
    head.type = 'button';
    const titleWrap = el('span', 'lev-build__category-title-wrap');
    titleWrap.appendChild(el('h5', 'lev-build__category-title', categoryName));
    head.appendChild(titleWrap);
    const chevron = el('span', 'lev-build__category-chevron');
    chevron.setAttribute('aria-hidden', 'true');
    head.appendChild(chevron);
    box.appendChild(head);

    const body = el('div', 'lev-build__category-body');
    const cards = items.map((item) => {
      const card = createItemCard(ctx, item, { showToggle: true, isMainItem: false });
      body.appendChild(card.el);
      return card;
    });
    box.appendChild(body);

    head.addEventListener('click', () => {
      state.catOpen[categoryName] = !state.catOpen[categoryName];
      sync();
      if (state.catOpen[categoryName]) {
        setTimeout(() => {
          const scroller = ctx.root.querySelector('[data-build-scroll]');
          const boxRect = box.getBoundingClientRect();
          if (window.innerWidth <= 570) {
            if (boxRect.bottom > window.innerHeight) {
              window.scrollBy({ top: boxRect.bottom - window.innerHeight + 40, behavior: 'smooth' });
            }
          } else if (scroller) {
            const scrollRect = scroller.getBoundingClientRect();
            if (boxRect.bottom > scrollRect.bottom) {
              scroller.scrollBy({ top: boxRect.bottom - scrollRect.bottom + 20, behavior: 'smooth' });
            }
          }
        }, 150);
      }
    });

    function update() {
      const hasSelected = items.some((item) => state.items[item.name]);
      box.classList.toggle('has-selected', hasSelected);
      box.classList.toggle('is-open', Boolean(state.catOpen[categoryName]));
      cards.forEach((card) => card.update());
    }

    return { el: box, update, chevron };
  }

  // ---------------------------------------------------------------------------
  // Resumo (tab 3)
  // ---------------------------------------------------------------------------

  function createSummary(ctx) {
    const { root, data, state, sync } = ctx;
    const rules = data.compatibilityRules;
    const wrapper = el('div', 'lev-build__summary');

    function update() {
      wrapper.innerHTML = '';
      const sections = [
        ['Bike', data.main || []],
        ['Acessórios', (data.accessories || []).filter((item) => isItemAllowed(item, rules))],
        ['Serviços', (data.services || []).filter((item) => isItemAllowed(item, rules))],
      ];

      sections.forEach(([title, items]) => {
        const selected = items.filter((item) => state.items[item.name]);
        if (!selected.length) return;
        const group = el('div', 'lev-build__summary-group');
        group.appendChild(el('div', 'lev-build__summary-title', title));
        selected.forEach((item) => {
          const sku = state.items[item.name];
          const row = el('div', 'lev-build__summary-row');

          const name = el('div', 'lev-build__summary-name');
          const nameInner = el('div');
          nameInner.append(`${item.name} `);
          nameInner.appendChild(el('span', 'bold', selectedLabel(item, sku)));
          name.appendChild(nameInner);
          row.appendChild(name);

          const price = el('div', 'lev-build__summary-price');
          price.append(formatPrice(priceFor(data, sku)));
          const remove = el('button', 'lev-build__remove');
          remove.type = 'button';
          remove.setAttribute('aria-label', `Remover ${item.name}`);
          const trash = document.createElement('img');
          trash.src = root.__levBuildAssets['item-trash'] || '';
          trash.alt = 'Remover item';
          remove.appendChild(trash);
          remove.addEventListener('click', () => {
            delete state.items[item.name];
            sync();
          });
          price.appendChild(remove);
          row.appendChild(price);
          group.appendChild(row);
        });
        wrapper.appendChild(group);
      });
    }

    return { el: wrapper, update };
  }

  // ---------------------------------------------------------------------------
  // Conteúdo por aba
  // ---------------------------------------------------------------------------

  function buildTabContent(ctx) {
    const { root, data, state } = ctx;
    const content = root.querySelector('[data-build-content]');
    content.innerHTML = '';
    const rules = data.compatibilityRules;
    const updaters = [];
    ctx.categoryChevrons = [];

    if (state.tab === 0) {
      (data.main || []).forEach((item) => {
        const card = createItemCard(ctx, item, { showToggle: false, isMainItem: true });
        content.appendChild(card.el);
        updaters.push(card.update);
      });
    } else if (state.tab === 1) {
      const visible = (data.accessories || []).filter((item) => isItemAllowed(item, rules));
      const grouped = {};
      const uncategorized = [];
      visible.forEach((item) => {
        if (item.category) {
          (grouped[item.category] = grouped[item.category] || []).push(item);
        } else {
          uncategorized.push(item);
        }
      });

      if (!visible.length) {
        content.appendChild(el('p', 'lev-build__empty', 'Nenhum acessório disponível para este modelo.'));
      }

      Object.entries(grouped).forEach(([categoryName, items]) => {
        const category = createCategory(ctx, categoryName, items);
        content.appendChild(category.el);
        updaters.push(category.update);
        ctx.categoryChevrons.push(category.chevron);
      });

      uncategorized.forEach((item) => {
        const card = createItemCard(ctx, item, { showToggle: true, isMainItem: false });
        content.appendChild(card.el);
        updaters.push(card.update);
      });
    } else {
      const summary = createSummary(ctx);
      content.appendChild(summary.el);
      updaters.push(summary.update);
    }

    ctx.contentUpdaters = updaters;
  }

  // ---------------------------------------------------------------------------
  // Footer
  // ---------------------------------------------------------------------------

  function setupFooter(ctx) {
    const { root, data, state, sync } = ctx;
    const prev = root.querySelector('[data-build-prev]');
    const next = root.querySelector('[data-build-next]');
    const prevLabel = prev.querySelector('[data-button-label]');
    const nextLabel = next.querySelector('[data-button-label]');

    const scrollTop = () => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 200);
    };

    prev.addEventListener('click', () => {
      state.tab = Math.max(0, state.tab - 1);
      scrollTop();
      ctx.onTabChanged();
    });
    next.addEventListener('click', () => {
      if (state.tab < 2) {
        state.tab += 1;
        scrollTop();
        ctx.onTabChanged();
      } else {
        submitBuild(ctx);
      }
    });

    return () => {
      const entries = selectedEntries(data, state);
      const total = entries.reduce((sum, entry) => sum + priceFor(data, entry.sku), 0);
      // Prazo do kit é o do item mais demorado, não a soma: a montagem é
      // paralela. Com a soma, a seleção padrão (quadro+pneu+banco, 3 dias cada)
      // mostrava "Montagem 9 dias!" onde a referência mostra 3.
      const days = entries.reduce((max, entry) => Math.max(max, leadTimeFor(data, entry.sku)), 0);
      root.querySelector('[data-build-total]').textContent = `R$ ${total.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      // Campaign prices by payment method (snippets/lev-payment-offer.liquid); the line exists only while the theme setting is on.
      const offerLine = root.querySelector('[data-build-offer]');
      if (offerLine && window.LevPaymentOffer) {
        const cents = Math.round(total * 100);
        offerLine.textContent = cents > 0 ? window.LevPaymentOffer.text('build', window.LevPaymentOffer.prices(cents)) : '';
      }
      // "Montagem 0 dia!" saía com o carrinho vazio — só 1 é singular.
      root.querySelector('[data-build-days]').textContent = `Montagem ${days} dia${days === 1 ? '' : 's'}!`;

      prev.hidden = state.tab === 0;
      next.classList.toggle('only-next', state.tab === 0);
      nextLabel.textContent = state.tab === 2 ? 'Finalizar' : 'Próximo';
      prevLabel.textContent = 'Anterior';
    };
  }

  // ---------------------------------------------------------------------------
  // Add To Cart — valida e envia via Cart AJAX API
  // ---------------------------------------------------------------------------

  async function submitBuild(ctx) {
    if (ctx.submitting || ctx.uncertain) return;
    const { root, data, state } = ctx;
    const rules = data.compatibilityRules;

    // 1. Validação por modelo: acessórios bloqueados (nível coarso)
    if (rules) {
      const modelViolations = selectedEntries(data, state).filter(({ item }) => !isItemAllowed(item, rules));
      if (modelViolations.length) {
        const names = modelViolations.map(({ item }) => item.name).join(', ');
        showBuildError(root, `Os seguintes acessórios não são compatíveis com ${data.name || 'este modelo'}: ${names}. Por favor remova-os antes de finalizar.`);
        return;
      }
    }

    // 2. Validação por SKU: conflitos entre combinações selecionadas (nível fino)
    if (hasAnyConflict(data, state)) {
      showBuildError(root, 'Existem itens incompatíveis na sua configuração. Verifique os itens marcados em vermelho e resolva os conflitos antes de finalizar.');
      return;
    }

    const selections = selectedEntries(data, state).map(({ item, sku }) => ({
      name: item.name,
      sku,
      label: selectedLabel(item, sku),
      variantId: variantInfo(data, sku).id,
      // Elevação, encosto e apoiador são produtos próprios: vão como linha
      // separada, que é como o estoque e a montagem os enxergam (LEV-22).
      addons: variantInfo(data, sku).addons || [],
    }));

    // Salva configuração local para uso pós-checkout (opcional)
    try {
      sessionStorage.setItem('lev_cruiser_build', JSON.stringify({ model: data.model, selections }));
    } catch (error) {
      console.warn('[LEV build] Nao foi possivel salvar a configuracao local', error);
    }

    // Propriedades de line item agrupam os componentes do KIT no carrinho,
    // pedido e ERP. "Kit" é visível ao cliente; "_kit" (oculta) é a chave técnica.
    const kitProperties = data.kit
      ? { Kit: data.kit.title, _kit: data.kit.handle }
      : { Kit: data.name || 'Monte sua Bike', _kit: data.model || 'build' };

    kitProperties._kit_instance = crypto.randomUUID();

    const items = selections
      .filter((selection) => selection.variantId)
      .flatMap((selection) => [
        {
          id: selection.variantId,
          quantity: 1,
          properties: { ...kitProperties, _kit_item: selection.name },
        },
        ...selection.addons
          .filter((addon) => addon.id)
          .map((addon) => ({
            id: addon.id,
            quantity: 1,
            properties: { ...kitProperties, _kit_item: `${selection.name} — ${addon.name}` },
          })),
      ]);

    // Se algum SKU não tem variant ID mapeado, instrui o operador.
    // Conta seleções, não linhas: um banco com encosto manda duas.
    const validVariantId = id => Number.isSafeInteger(Number(id)) && Number(id) > 0;
    const missingVariant = selections.filter(selection => !validVariantId(selection.variantId) || selection.addons.some(addon => !validVariantId(addon.id)));
    if (!items.length || missingVariant.length) {
      showBuildError(root, document.documentElement.dataset.kitIncomplete);
      return;
    }

    // 3. POST para /cart/add.js com todos os itens como produtos reais
    const cartSession = window.LevCartSession;
    const token = cartSession?.begin();
    if (!token) {
      showBuildError(root, document.documentElement.dataset.cartUncertain);
      return;
    }
    const next = root.querySelector('[data-build-next]');
    const nextLabel = next?.querySelector('[data-button-label]');
    if (next) {
      next.disabled = true;
      if (nextLabel) nextLabel.textContent = 'Adicionando…';
    }

    ctx.submitting = true;
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal: AbortSignal.timeout(20000),
      });

      // Even HTTP 422 can mean a partial addition. Never retry a write based only on its status.
      if (!response.ok) throw new Error('Cart addition requires review');
      const result = await response.json();
      const remaining = Array.isArray(result?.items) ? [...result.items] : [];
      const complete = items.every(expected => {
        const index = remaining.findIndex(actual => actual && Number(actual.id) === Number(expected.id) && actual.quantity === expected.quantity && Object.entries(expected.properties).every(([key, value]) => actual.properties?.[key] === value));
        if (index < 0) return false;
        remaining.splice(index, 1);
        return true;
      });
      if (!complete || remaining.length) throw new Error('Incomplete cart confirmation');

      // Finalizar leva direto para o checkout, igual ao botão do sidecart —
      // a parada no /cart era um passo a mais no meio do fluxo.
      ctx.uncertain = true;
      window.location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
    } catch (error) {
      cartSession.finish(token, false);
      ctx.uncertain = true;
      const notice = showBuildError(root, document.documentElement.dataset.cartUncertain, { persistent: true });
      const review = document.createElement('a');
      review.href = (window.Shopify?.routes?.root || '/') + 'cart';
      review.textContent = document.documentElement.dataset.cartView;
      notice.appendChild(review);
    } finally {
      ctx.submitting = false;
      if (next) {
        next.disabled = Boolean(ctx.uncertain);
        if (nextLabel) nextLabel.textContent = 'Finalizar';
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Inicialização
  // ---------------------------------------------------------------------------

  async function loadBuildData(root) {
    const status = root.querySelector('[data-build-load-status]');
    const next = root.querySelector('[data-build-next]');
    root.querySelector('.lev-build__error')?.remove();
    root.setAttribute('aria-busy', 'true');
    if (status) status.hidden = false;
    if (next) next.disabled = true;
    try {
      const response = await fetch(root.dataset.buildDataUrl, { signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Build data unavailable');
      const data = await response.json();
      if (!Array.isArray(data?.main) || !data.main.length || !data.main.every(group => typeof group?.name === 'string' && Array.isArray(group.combinations) && group.combinations.length)) throw new Error('Invalid build data');
      return data;
    } catch (error) {
      const notice = showBuildError(root, root.dataset.buildLoadError, { persistent: true });
      const retry = el('button', 'lev-build__button', root.dataset.buildRetry);
      retry.type = 'button';
      retry.addEventListener('click', () => { retry.disabled = true; init(root); }, { once: true });
      notice.appendChild(retry);
      return null;
    } finally {
      root.setAttribute('aria-busy', 'false');
      if (status) status.hidden = true;
    }
  }

  async function init(root) {
    const assets = parseJsonScript(root, '[data-build-assets]', {});
    root.__levBuildAssets = assets;

    let data = await loadBuildData(root);
    if (!data) return;

    // Contexto do produto KIT (presente quando a section roda no template product.build)
    data.kit = parseJsonScript(root, '[data-build-kit]', null);

    // Acessórios/serviços cadastrados como produtos referenciados no produto KIT
    data = mergeKitProducts(data, parseJsonScript(root, '[data-build-kit-products]', null), assets);

    // Peças escolhidas nos seletores do produto pai. Vem por último entre os
    // merges porque é a fonte mais forte: sobrescreve a peça inteira.
    data.variantMap = { ...(data.variantMap || {}) };
    data = applyPieceProducts(
      data,
      parseJsonScript(root, '[data-build-pieces]', null),
      assets,
      data.variantMap,
      parseJsonScript(root, '[data-build-seat-addons]', null)
    );

    // Os defaults do JSON apontam para códigos legados. Quando a peça passa a
    // vir de produto, esses códigos não existem mais — cai na primeira cor.
    const defaults = { ...(data.defaults || {}) };
    (data.main || []).forEach((group) => {
      const skus = (group.combinations || []).map((combo) => String(combo.sku));
      if (!skus.length) return;
      if (!skus.includes(String(defaults[group.name]))) defaults[group.name] = skus[0];
    });

    const state = {
      tab: 0,
      items: { ...defaults },
      options: {},
      open: {},
      catOpen: {},
      bancoWarning: {},
    };
    initOptionStates(data, state);
    cleanBlockedSelections(data, state);

    const intro = root.querySelector('[data-build-intro]');
    if (intro && data.intro) intro.textContent = data.intro;

    const ctx = { root, data, state, assets, contentUpdaters: [], categoryChevrons: [] };

    const sync = () => {
      ctx.contentUpdaters.forEach((update) => update());
      updateBike(root, data, state, assets);
      updateTabs();
      updateFooter();
      const warning = root.querySelector('[data-build-warning]');
      if (warning) warning.hidden = !hasAnyConflict(data, state);
    };
    ctx.sync = sync;

    ctx.onTabChanged = () => {
      buildTabContent(ctx);
      sync();
    };

    const updateTabs = setupTabs(root, data, state, (index) => {
      if (state.tab === index) return;
      state.tab = index;
      ctx.onTabChanged();
    });
    const updateFooter = setupFooter(ctx);
    setupModels(root, data);

    // No mobile o seletor de modelos vive dentro da área de conteúdo (antes das
    // tabs) e rola junto; no desktop fica acima do container — igual ao original.
    const modelWrap = root.querySelector('[data-model-wrap]');
    const scrollArea = root.querySelector('[data-build-scroll]');
    const panel = root.querySelector('.lev-build__panel');
    const mobileQuery = window.matchMedia('(max-width: 570px)');
    const placeModelNav = () => {
      if (!modelWrap || !scrollArea || !panel) return;
      if (mobileQuery.matches) {
        scrollArea.insertBefore(modelWrap, scrollArea.firstChild);
      } else if (modelWrap.parentElement !== panel) {
        panel.insertBefore(modelWrap, panel.querySelector('.lev-build__container'));
      }
    };
    placeModelNav();
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', placeModelNav);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(placeModelNav);
    }

    // Tooltip do aviso de incompatibilidade: hover no desktop, clique no mobile
    const warning = root.querySelector('[data-build-warning]');
    let warningTimer = null;
    warning?.addEventListener('click', () => {
      warning.classList.toggle('is-open');
      clearTimeout(warningTimer);
      if (warning.classList.contains('is-open')) {
        warningTimer = setTimeout(() => warning.classList.remove('is-open'), 5000);
      }
    });

    // Jiggle dos chevrons das categorias: a cada 10s, anima por 3s (igual ao original)
    setInterval(() => {
      const chevrons = ctx.categoryChevrons || [];
      chevrons.forEach((chevron) => chevron.classList.add('is-jiggling'));
      setTimeout(() => chevrons.forEach((chevron) => chevron.classList.remove('is-jiggling')), 3000);
    }, 10000);

    // Sequência de introdução do site original:
    // tooltip visível até 5,5s; primeiro card expande aos 2s e recolhe aos 4,5s
    const firstMain = (data.main || [])[0]?.name;
    if (firstMain) {
      setTimeout(() => {
        state.open[firstMain] = true;
        sync();
      }, 2000);
      setTimeout(() => {
        state.open[firstMain] = false;
        sync();
      }, 4500);
    }
    if (intro) {
      setTimeout(() => {
        intro.hidden = true;
      }, 5500);
    }

    buildTabContent(ctx);
    sync();
    preloadAssets(data, assets);
    const next = root.querySelector('[data-build-next]');
    if (next) next.disabled = false;
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-lev-cruiser-build]').forEach(init);
  });
})();
